import * as API from 'services/api';
import { Movie, Person } from 'types/prediction-engine';

export const predictionEngineLoadMovies = async ({
  movieIds,
  limited,
  castRuleVersion,
}: {
  movieIds: string[];
  limited: boolean;
  castRuleVersion: string | null;
}) => {
  try {
    const url = `/v1/prediction-engine/movies-data/`;

    const res = await API.post(url, {
      movies_ids: movieIds,
      limited,
      cast_rule_version: castRuleVersion,
    });

    return res.data as {
      movies: Movie[];
      model_id: string;
    };
  } catch (error: any) {
    return {
      error: error?.response?.data?.message || 'Failed to load movies',
      status: error?.response?.status ?? '',
    };
  }
};

export type AvailableModel = {
  id: string;
  algo_version: string;
  data_version: string;
  limited_cast: boolean;
  is_organization_model: boolean;
  is_domestic: boolean;
  predicting_limit_date: string; // YYYY-MM-DD
  cast_rule_version: string | null; // 'lc_v2' for LC2 models
};

export const fetchAvailableModels = async (): Promise<AvailableModel[]> => {
  const url = `/v1/prediction-engine/available-models/`;
  const res = await API.get(url);
  return res.data.models;
};

export const predictionEnginePredict = async ({
  movies,
  model_id,
}: {
  movies: Movie[];
  model_id: string;
}) => {
  try {
    const url = `/v1/prediction-engine/predict/`;
    const res = await API.post(url, { movies, model_id });
    return res.data as {
      movies: Movie[];
      model_id: string;
    };
  } catch (error: any) {
    return Promise.reject({
      error: error?.response?.data?.message || 'Failed to predict',
      status: error?.response?.status ?? '',
    });
  }
};

export const getPridectionNameSuggestions = async (
  q: string,
  suggestionType: string,
) => {
  try {
    const url = `/v1/prediction-engine/names-search/?name=${q}&role_type=${suggestionType}`;
    const res = await API.get(url);
    return res.data;
  } catch (error) {
    return [];
  }
};

export const predictionEngineSave = async ({
  movies,
  model_id,
}: {
  movies: Movie[];
  model_id: string;
}) => {
  const url = `/v1/prediction-engine/save-predictions/`;

  const res = await API.post(url, { movies, model_id });

  return res.data as {
    movies: Movie[];
    model_id: string;
  };
};

export const fetchPersonsData = async ({
  personsIds,
}: {
  personsIds: string[];
}) => {
  const url = `/v1/prediction-engine/persons-data/`;
  const res = await API.post(url, { persons_ids: personsIds });
  return res.data as { [key: string]: Person };
};

export const checkMoviesData = async ({
  moviesIds,
}: {
  moviesIds: string[];
}) => {
  const url = `/v1/prediction-engine/movies-data-check/`;
  const res = await API.post(url, { movies_ids: moviesIds });
  return res.data as { [key: string]: any };
};

export const predictionEngineMoviesSearch = async (q: string) => {
  try {
    const url = `/v1/prediction-engine/movies-search/?q=${encodeURIComponent(q)}`;
    const res = await API.get(url);
    return res.data || [];
  } catch (error) {
    return [];
  }
};
